// -----------------------------------------------------------------------
// <copyright file="GetHelpCommonSearches.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import {
  IBaseProps,
  IIconStyles,
  ILinkStyles,
  IStyleFunctionOrObject,
  ITextStyles,
  ITheme,
  Icon,
  Link,
  Stack,
  styled,
} from '@fluentui/react';
import React from 'react';
import { useDispatch } from 'react-redux';
import { setVsbUpsellAlertActivated } from '../../../web-legacy/packages/app/appActions';
import { setOriginalQuery } from '../../../web-legacy/packages/contactUs/reduxUpdater/actions/autoSuggestActions';
import { initializeModalityStateOnGetHelp } from '../../../web-legacy/packages/contactUs/reduxUpdater/actions/modalitySectionActions';
import { formSubmission } from '../../../web-legacy/packages/contactUs/reduxUpdater/actions/sharedStateActions';
import { dispatchAlchemyFetch } from '../../../web-legacy/packages/contactUs/reduxUpdater/dispatchers/contactUsDispatchers';
import { StyledText } from '../../common/components/StyledText';
import { appContext } from '../../models/getHelp/appContext';
import { getRandomGuidString } from '../../utils/getHelputils';
import { resolveStyles } from '../../utils/resolveStyles';
import { getString } from '../../utils/stringLoader';

interface ICommonSearchItemStyleProps {
  theme: ITheme;
}

interface ICommonSearchItemStyles {
  icon: IIconStyles;
  link: ILinkStyles;
}

interface ICommonSearchItemProps extends IBaseProps, React.HTMLAttributes<HTMLElement> {
  text: string;
  styles?: IStyleFunctionOrObject<ICommonSearchItemStyleProps, ICommonSearchItemStyles>;
  theme?: ITheme;
}

const commonSearchItemDefaultStyles = (props: ICommonSearchItemStyleProps): ICommonSearchItemStyles => ({
  icon: {
    root: {
      width: '14px',
      height: '14px',
      fontWeight: '400',
      marginRight: '8px',
      marginTop: '8px',
      color: props.theme.palette.themeDarkAlt,
    },
  },
  link: {
    root: {
      marginTop: '4px',
      marginBottom: '4px',
      height: '20px',
      fontSize: '14px',
      fontWeight: '400',
      color: props.theme.palette.themeDarkAlt,
      selectors: {
        '&:hover, &:focus': {
          cursor: 'pointer',
          color: props.theme.palette.themeDarkAlt,
        },
      },
    },
  },
});

const CommonSearchItemBase = (props: ICommonSearchItemProps): JSX.Element => {
  const dispatch = useDispatch();

  const onSuggestionClick = React.useCallback((queryText: string): void => {
    // Set the sessionId to a new Guid every time user click a common searches query
    appContext.sessionId = getRandomGuidString();
    // ConvergenceToDo: add logs await logCommonSearchesQueryClick(queryText);
    // Send query to searchbox
    dispatch(formSubmission(queryText));
    dispatch(dispatchAlchemyFetch(queryText));
    dispatch(initializeModalityStateOnGetHelp(null));
    dispatch(setOriginalQuery(queryText));
    dispatch(setVsbUpsellAlertActivated());
  }, []);

  const styles = resolveStyles(
    {
      theme: props.theme,
    },
    props.styles
  );

  return (
    <Stack horizontal>
      <Icon iconName="Search" styles={styles.icon} />
      <Link
        styles={styles.link}
        // tslint:disable-next-line jsx-no-lambda
        onClick={() => onSuggestionClick(props.text)}
      >
        {props.text}
      </Link>
    </Stack>
  );
};

const CommonSearchItem: React.FunctionComponent<ICommonSearchItemProps> = styled(
  CommonSearchItemBase,
  commonSearchItemDefaultStyles
);

const commonSearchesHeaderStyle: ITextStyles = {
  root: {
    height: '20px',
    padding: '10px 0px 6px 0px',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '20px',
  },
};

const commonSearchTexts = [
  'Set up scanner in Windows',
  'Fix ethernet connection problems',
  'How to install Office',
  'Troubleshoot activation errors in Office',
];

export const commonSearchTextCount = commonSearchTexts.length;

export const GetHelpCommonSearches = (): JSX.Element => {
  return (
    <>
      <StyledText styles={commonSearchesHeaderStyle} tabIndex={0} role="heading" aria-level={2}>
        {getString('GetHelp_CommonSearchesHeader')}
      </StyledText>
      <Stack>
        {commonSearchTexts.map((searchText, index) => (
          <CommonSearchItem text={searchText} key={index} />
        ))}
      </Stack>
    </>
  );
};
